import React, { useCallback, useEffect, useState } from 'react';
import {
  Card,
  Typography,
  Button,
  Space,
  Table,
  Statistic,
  Row,
  Col,
  message,
  Alert,
  QRCode,
  Input,
  Divider,
  Empty
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { CopyOutlined, HomeOutlined, ReloadOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import '../pages/MemberManagement.css';

const { Text, Paragraph, Title } = Typography;

type Conversion = {
  _id: string;
  teamName: string;
  teamCode?: string;
  invitedName?: string;
  createdAt: string;
};

type PromoData = {
  merchantCode: string;
  merchantName: string;
  inviteUrl: string;
  reviewApproved: boolean;
  inviteCount: number;
  conversions: Conversion[];
};

async function merchantFetch(path: string) {
  const token = localStorage.getItem('token');
  const res = await fetch(`/api/merchant${path}`, {
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {})
    }
  });
  return res.json();
}

const MerchantPromo: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<PromoData | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const body = await merchantFetch('/promo');
      if (!body.success) {
        message.error(body.error || '加载失败');
        setData(null);
        return;
      }
      setData(body.data);
    } catch {
      message.error('网络异常');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const copyLink = async () => {
    if (!data?.inviteUrl) return;
    try {
      await navigator.clipboard.writeText(data.inviteUrl);
      message.success('推广链接已复制');
    } catch {
      message.error('复制失败，请手动选择链接复制');
    }
  };

  const columns: ColumnsType<Conversion> = [
    {
      title: '入驻时间',
      dataIndex: 'createdAt',
      width: 170,
      render: (v) => (v ? dayjs(v).format('YYYY-MM-DD HH:mm') : '—')
    },
    {
      title: '团队名称',
      dataIndex: 'teamName',
      render: (v) => v || '—'
    },
    {
      title: '团队编码',
      dataIndex: 'teamCode',
      width: 160,
      render: (v) => v || '—'
    },
    {
      title: '创建人',
      dataIndex: 'invitedName',
      width: 120,
      render: (v) => v || '—'
    }
  ];

  return (
    <div className="page-container full-height members-lims">
      <nav className="mm-crumb" aria-label="breadcrumb">
        <HomeOutlined />
        <span>首页</span>
        <span className="mm-crumb-sep">/</span>
        <span>推广激励</span>
      </nav>

      <header className="mm-page-head d-flex flex-wrap justify-content-between align-items-start gap-3">
        <div>
          <h1 className="mm-title">推广激励</h1>
          <p className="mm-subtitle mb-0">
            分享专属链接或二维码，邀请未入驻团队注册并创建团队；成功后计入您的邀请成果。
          </p>
        </div>
        <Button icon={<ReloadOutlined />} onClick={() => void load()} loading={loading}>
          刷新
        </Button>
      </header>

      <div className="content-area px-1 px-md-2">
        {data && !data.reviewApproved && (
          <Alert
            className="mb-3"
            type="warning"
            showIcon
            message="商铺资料尚未通过平台审核"
            description="审核通过前，推广链接可能无法被正确归因。请先完成商铺中心资料审核。"
          />
        )}

        <Row gutter={16} className="mb-3">
          <Col xs={24} md={10}>
            <Card loading={loading} title="我的推广">
              <Paragraph type="secondary" className="mb-2">
                商家编号：<Text strong>{data?.merchantCode || '—'}</Text>
              </Paragraph>
              <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 16 }}>
                {data?.inviteUrl ? (
                  <QRCode value={data.inviteUrl} size={180} errorLevel="M" />
                ) : (
                  <Empty description="暂无推广码" />
                )}
              </div>
              <Space.Compact style={{ width: '100%' }}>
                <Input readOnly value={data?.inviteUrl || ''} />
                <Button type="primary" icon={<CopyOutlined />} onClick={() => void copyLink()}>
                  复制
                </Button>
              </Space.Compact>
              <Paragraph type="secondary" style={{ marginTop: 12, marginBottom: 0, fontSize: 12 }}>
                受邀用户打开链接后注册，并在登录后「创建团队」成功，即记为一次有效邀请（每个团队仅计一次）。
              </Paragraph>
            </Card>
          </Col>
          <Col xs={24} md={14}>
            <Card loading={loading}>
              <Statistic title="成功邀请团队数" value={data?.inviteCount || 0} />
              <Divider />
              <Title level={5}>规则说明（一期）</Title>
              <ul style={{ paddingLeft: 18, marginBottom: 0, color: 'rgba(0,0,0,0.65)' }}>
                <li>通过您的链接进入平台并完成账号注册；</li>
                <li>该用户登录后创建新团队（成为团队管理员）；</li>
                <li>系统记录团队名称与入驻时间，计入您的邀请成果；</li>
                <li>激励奖励规则将在后续版本开放，当前先沉淀邀请数据。</li>
              </ul>
            </Card>
          </Col>
        </Row>

        <div className="am-table-shell bg-white rounded-3 border p-2 p-md-3">
          <Title level={5} style={{ marginTop: 4 }}>
            成功邀请记录
          </Title>
          <Table
            rowKey="_id"
            loading={loading}
            columns={columns}
            dataSource={data?.conversions || []}
            pagination={{ pageSize: 10 }}
            locale={{ emptyText: '暂无成功邀请，快去分享推广链接吧' }}
          />
        </div>
      </div>
    </div>
  );
};

export default MerchantPromo;
